import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getUsersTotalLimits} from 'src/openapi/admin/apis/UserSearchLimitApi';
import {useAppDispatch} from 'src/store/hooks';

import {COLUMNS, TOTAL_LIMIT_COLUMNS, USED_LIMIT_COLUMNS} from '../constants';
import {selectSelectedColumns} from '../slices/user-search-result-columns.slice';
import {
    userSearchResultLimitsActions,
    userSearchResultLimitsDataSelector,
    userSearchResultLimitsResultSelector,
} from '../slices/user-search-result-limits.slice';
import {userSearchResultStateSelector} from '../slices/user-search-result-state.slice';
import {checkShouldLoadColumn} from '../utils';

import type {UserSearchResultCacheKeys} from '../typedef';
import type {UserSearchLimit} from 'src/openapi/admin/models/UserSearchLimit';


type Props = {
    userIds: number[],
    totalCount: number,
    columnId: string,
};

export const useGetUserSearchResultLimits = ({userIds, totalCount, columnId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const selectedColumns = useSelector(selectSelectedColumns);

    const getAction = useCallback(() => apiDispatch(getUsersTotalLimits({
        usersTotalLimitRequest: {
            usersId: userIds,
            totalLimitColumn: selectedColumns
                .filter(column => TOTAL_LIMIT_COLUMNS.includes(column))
                .map(columnValue => Object
                    .entries(COLUMNS)
                    .find(([, value]) => value === columnValue)?.[0] || '',
                ),
            usedLimitColumn: selectedColumns
                .filter(column => USED_LIMIT_COLUMNS.includes(column))
                .map(columnValue => Object
                    .entries(COLUMNS)
                    .find(([, value]) => value === columnValue)?.[0] || '',
                ),
        },
    })), [userIds, selectedColumns, apiDispatch]);

    const setLoadedData = useCallback((
        response: Record<string, UserSearchLimit[]>,
        dataCacheKeys: UserSearchResultCacheKeys,
    ) => {
        dispatch(userSearchResultLimitsActions.onSuccess(({
            cacheKeys: dataCacheKeys,
            totalCount,
            data: response,
        })));
    }, [totalCount, dispatch]);

    const shouldUpdateData = useMemo(
        () => checkShouldLoadColumn(columnId, selectedColumns),
        [columnId, selectedColumns],
    );

    return useCachedTable({
        dataSelector: userSearchResultLimitsDataSelector,
        stateSelector: userSearchResultStateSelector,
        resultSelector: userSearchResultLimitsResultSelector,
        actions: userSearchResultLimitsActions,
        getAction,
        onSuccessLoad: setLoadedData,
        shouldClearOnUnmount: false,
        shouldUpdateData,
    });
};
